import { Component, OnInit, ViewChild } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { SettingsService } from '../../services';

export interface EthosImportLog {
  id: string;
  startTime: string;
  endTime: string;
  duration: string;
  totalVehicles: number;
  processedVehicles: number;
  skippedVehicles: number;
  errorCount: number;
  status: string;
  triggerType: string;
  vscProductSummary: { [key: string]: number };
  errors?: string[];
}

@Component({
  selector: 'app-ethos-import-logs',
  templateUrl: './ethos-import-logs.component.html',
  styleUrls: ['./ethos-import-logs.component.scss']
})
export class EthosImportLogsComponent implements OnInit {
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  displayedColumns: string[] = [
    'startTime',
    'status',
    'triggerType',
    'totalVehicles',
    'processedVehicles',
    'skippedVehicles',
    'errorCount',
    'duration'
  ];

  dataSource: MatTableDataSource<EthosImportLog>;
  loading = true;
  currentStatus: any = null;
  refreshInterval: any;

  constructor(
    private dialogRef: MatDialogRef<EthosImportLogsComponent>,
    private settingsService: SettingsService
  ) {
    this.dataSource = new MatTableDataSource<EthosImportLog>([]);
  }

  ngOnInit(): void {
    this.loadLogs();
    this.checkCurrentStatus();
    
    // Refresh every 5 seconds
    this.refreshInterval = setInterval(() => {
      this.checkCurrentStatus();
      // Refresh logs if there's a running import or if we just detected one
      if (this.currentStatus?.status === 'running') {
        this.loadLogs();
      }
    }, 5000);
  }

  ngOnDestroy(): void {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }
  }

  loadLogs(): void {
    this.settingsService.getEthosImportLogs().subscribe({
      next: (response) => {
        this.dataSource.data = response.logs || [];
        this.dataSource.paginator = this.paginator;
        this.dataSource.sort = this.sort;
        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading Ethos import logs:', error);
        this.loading = false;
      }
    });
  }

  checkCurrentStatus(): void {
    this.settingsService.getEthosImportStatus().subscribe({
      next: (status) => {
        this.currentStatus = status;
      },
      error: (error) => {
        console.error('Error checking import status:', error);
      }
    });
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'running':
        return 'status-running';
      case 'completed':
        return 'status-completed';
      case 'failed':
        return 'status-failed';
      case 'completed_with_errors':
        return 'status-warning';
      default:
        return '';
    }
  }

  getStatusIcon(status: string): string {
    switch (status) {
      case 'running':
        return 'sync';
      case 'completed':
        return 'check_circle';
      case 'failed':
        return 'error';
      case 'completed_with_errors':
        return 'warning';
      default:
        return 'help';
    }
  }

  formatDate(dateString: string): string {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString();
  }

  close(): void {
    this.dialogRef.close();
  }

  expandRow(log: EthosImportLog): void {
    // TODO: Implement row expansion to show VSC product summary and errors
    console.log('Expand row:', log);
  }

  clearLogs(): void {
    if (confirm('Are you sure you want to clear all Ethos import logs? This action cannot be undone.')) {
      this.loading = true;
      this.settingsService.clearEthosImportLogs().subscribe({
        next: () => {
          this.dataSource.data = [];
          this.currentStatus = null;
          this.loading = false;
          // Show success message (you could add a snackbar here if available)
          alert('All Ethos import logs have been cleared successfully.');
        },
        error: (error) => {
          console.error('Error clearing logs:', error);
          this.loading = false;
          alert('Failed to clear logs. Please try again.');
        }
      });
    }
  }
}